import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { AppSettings } from '@Shared/app.settings';
import { UserCommonApiService, UtilsService } from '@App/common/services';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { SearchLocationDirective } from '../../../common/directives/search-location.directive';
import { InputTextModule } from 'primeng/inputtext';
import { SearchAddressDirective } from '../../../common/directives/search-address.directive';
import { NoLeadingSpaceDirective } from '../../../common/directives/no-leading-space.directive';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'app-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    NoLeadingSpaceDirective,
    SearchAddressDirective,
    InputTextModule,
    SearchLocationDirective,
    AutoCompleteModule
  ],
  encapsulation: ViewEncapsulation.None
})
export class AddressInputComponent implements OnInit {
  @Input() styleClass: string = '';
  @Output() outputAddressForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();
  countryList: any[] = [];
  stateList: any[] = [];
  addressForm: FormGroup = new FormGroup({});
  @ViewChild('stateNames', { static: false }) stateNames: AutoComplete;
  filteredStatesList: any[];
  chosenCountry: string = 'US';
  private streetAddress: string;
  private selectedCity: any;

  constructor(
    private userCommonService: UserCommonApiService,
    private cdr: ChangeDetectorRef,
    private utilsService: UtilsService
  ) {}

  ngOnInit() {
    this.addressForm = new FormGroup({
      country: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      address2: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', [Validators.required]),
      zipCode: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(7)
      ])
    });
    this.getCountryList();
    this.addressForm.get('country').valueChanges.subscribe((country) => {
      this.chosenCountry = country.code;
      this.addressForm.get('state').reset();
      this.addressForm.get('city').reset();
      this.filterStates({ query: '' });
      this.addressForm
        .get('zipCode')
        .setValidators([
          Validators.required,
          Validators.pattern(
            this.addressForm.get('country').value?.code === 'CA'
              ? AppSettings.CANADA_ZIPCODE_PATTERN
              : AppSettings.US_ZIPCODE_PATTERN
          )
        ]);
    });
    this.addressForm.get('zipCode').valueChanges.subscribe((res) => {
      const upper = res?.toUpperCase?.() ?? '';
      if (res !== upper) {
        this.addressForm.get('zipCode').patchValue(upper, { emitEvent: false });
      }
    });
  }

  onBlur() {
    this.outputAddressForm.emit(this.addressForm);
  }

  setFormattedLocationAddress(addressEvent) {
    if (addressEvent) {
      const street_number = this.utilsService.getAddressComponent(
        addressEvent.address_components,
        'street_number'
      );
      const route = this.utilsService.getAddressComponent(
        addressEvent.address_components,
        'route'
      );
      const zip = this.utilsService.getAddressComponent(
        addressEvent.address_components,
        'postal_code'
      );

      if (street_number && route) {
        this.streetAddress = street_number + ' ' + route;
      } else if (
        !street_number &&
        !isNaN(+this.addressForm.get('address').value.split(' ')[0]) &&
        route
      ) {
        this.streetAddress =
          this.addressForm.get('address').value.split(' ')[0] + ' ' + route;
      } else {
        this.streetAddress = route;
      }
      this.addressForm.get('address').setValue(this.streetAddress || '');
      this.addressForm.get('address').markAsDirty();

      this.setFormattedLocationCity(addressEvent);

      this.addressForm.get('zipCode').setValue(zip);
      this.addressForm.get('zipCode').markAsDirty();

      if (!this.streetAddress) {
        this.addressForm.get('address').setErrors({ required: true });
      }
    }
  }

  setFormattedLocationCity(addressEvent) {
    if (addressEvent) {
      const city =
        this.utilsService.getAddressComponent(
          addressEvent.address_components,
          'locality'
        ) ||
        this.utilsService.getAddressComponent(
          addressEvent.address_components,
          'sublocality'
        ) ||
        '';
      const state = this.utilsService.getAddressComponent(
        addressEvent.address_components,
        'administrative_area_level_1'
      );
      const chosenCountry = this.addressForm.get('country').value;
      this.getCityByName(city, chosenCountry, state);
    }
  }

  setCity() {
    if (this.selectedCity) {
      this.addressForm.controls.city.setValue(this.selectedCity.label);
      this.cdr.detectChanges();
    }
  }

  filterStates({ query } = { query: '' }, autoOpenDropdown: boolean = false) {
    if (autoOpenDropdown) {
      this.stateNames.show();
    }
    if (this.stateNames) {
      this.stateNames.loading = false;
    }
    if (this.addressForm.value?.country?.id) {
      this.userCommonService
        .getStateByName(query, this.addressForm.value.country.id)
        .subscribe(({ data }) => {
          this.filteredStatesList = data;
          if (data?.length === 1) {
            this.addressForm.controls.state.setValue(data[0]);
            this.addressForm.controls.state.markAsDirty();
            this.addressForm.controls.state.markAsTouched();
            this.addressForm.controls.state.setErrors(null);
            this.stateNames.hide();
          }

          if (data?.length === 0) {
            this.addressForm.controls.state.reset();
            this.addressForm.controls.state.setValue('');
            this.addressForm.controls.state.setErrors({ notFound: true });
            this.stateNames.hide();
          }
        });
    }
  }

  checkValidState() {
    if (typeof this.addressForm.get('state').value === 'string') {
      this.addressForm.get('state').setErrors({ required: true });
    } else {
      this.addressForm.get('state').setErrors(null);
    }
  }

  private getCityByName(city: any, chosenCountry: any, state?: any) {
    this.userCommonService
      .getCityByName(city, chosenCountry.id, state)
      .subscribe({
        next: ({ data }) => {
          if (data) {
            this.selectedCity = {
              label: data.city.cityName,
              value: data.city.id,
              elm: data
            };
            this.setCity();
            if (data.state) {
              this.setState(data.state.stateName);
            } else if (state) {
              this.setState(state);
            }
          } else {
            this.addressForm.controls.city.reset();
          }
        },
        error: () => {
          this.addressForm.controls.city.setErrors({ notFound: true });
        }
      });
  }

  private setState(state: any, autoOpenDropdown: boolean = false) {
    if (state) {
      this.userCommonService
        .getStateByName(state, this.addressForm.value.country.id)
        .subscribe(({ data }) => {
          this.addressForm.get('state').setValue(data[0]);
          if (!data.length) {
            this.addressForm.get('state').setValue('');
            this.filterStates({ query: '' }, autoOpenDropdown);
          }
        });
    } else {
      this.addressForm.get('state').setValue('');
      this.filterStates({ query: '' }, autoOpenDropdown);
    }
  }

  private getCountryList() {
    this.utilsService.getCountryList().subscribe(({ data }) => {
      this.countryList = data;
      if (!this.addressForm.get('country').value) {
        this.addressForm.get('country').setValue(this.countryList[0]);
      }
    });
  }
}
